import { useMemo } from "react";

import { useAlertContext, useFormikWithDirtyForm } from "@shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AmsParametersResponse, amsParametersApi } from "@/api";
import { nameof } from "@/common/helper/commonHelper";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

import { AmsParametersFormValues } from "../AmsParametersSettings.types";
import { amsParametersValidationSchema } from "../AmsParametersSettings.utils";

function toFormValues(data: AmsParametersResponse | undefined): AmsParametersFormValues {
  // The backend allows incomplete mappings, so zipCode/vid can come back null despite the generated type.
  const mappings = (data?.mappings ?? []) as Array<{ id: string; zipCode?: string | null; vid?: string | null }>;

  return {
    sid: data ? String(data.sid) : "",
    mappings: mappings.map((mapping) => ({ id: mapping.id, zipCode: mapping.zipCode ?? "", vid: mapping.vid ?? "" })),
  };
}

export function useAmsParametersSettings() {
  const { t } = useTranslation();
  const { showSuccess } = useAlertContext();
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: [QueryKey.AmsParameters],
    queryFn: () => amsParametersApi.getAmsParameters(),
    staleTime: 0,
    refetchOnMount: "always",
  });

  const initialValues = useMemo<AmsParametersFormValues>(() => toFormValues(data), [data]);

  const saveMutation = useMutation({
    mutationFn: (values: AmsParametersFormValues) =>
      amsParametersApi.updateAmsParameters({
        updateAmsParametersRequest: {
          sid: Number(values.sid.trim()),
          mappings: values.mappings.map((mapping) => ({
            id: mapping.id,
            zipCode: mapping.zipCode.trim(),
            vid: mapping.vid.trim(),
          })),
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.AmsParameters] });
      showSuccess(t("settings.amsParameters.saveSuccess"));
    },
  });

  const validationSchema = useMemo(() => amsParametersValidationSchema(t), [t]);

  const formik = useFormikWithDirtyForm<AmsParametersFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: (values) => saveMutation.mutateAsync(values),
  });

  const handleAddMapping = () => {
    void formik.setFieldValue(nameof<AmsParametersFormValues>("mappings"), [
      ...formik.values.mappings,
      { id: null, zipCode: "", vid: "" },
    ]);
  };

  const handleDeleteMapping = (index: number) => {
    void formik.setFieldValue(
      nameof<AmsParametersFormValues>("mappings"),
      formik.values.mappings.filter((_, i) => i !== index)
    );
  };

  return {
    formik,
    isSaving: saveMutation.isPending,
    handleAddMapping,
    handleDeleteMapping,
  };
}
